import { Element } from "MyDashboards/state/editorTypes";
import React from "react";
import {
  TDashboardComponent,
  TDashboardComponentRegistry,
  TDashboardComponentTypes,
} from "./types";

const createComponentRegistry = (
  Default: TDashboardComponent,
  components: Record<string, TDashboardComponent>
): TDashboardComponentRegistry => {
  const registry = {
    components: { ...components },
  };

  const renderResults: Record<string, Element | null> = {};

  /**
   *
   * Steps to render elements:
   *
   * 1. Add all pages on the view to be ready for rendered
   * 2. walk all elements and mark them ready to be sent or await the render results if necessary
   *
   * Questions? Should each component also return the rendering result as Element which is sendable to the backend?
   *
   *
   */

  return {
    Edit: (props: {
      el: Element;
      onChange: (el: Partial<Element>) => void;
    }) => {
      // select the renderer
      const { el } = props;
      if (el.component || el.type) {
        /**
         * Pick a component from the registry and edit the element using it
         */
        const Comp = registry.components[el.component?.component || el.type];
        if (Comp) {
          return <Comp.Edit el={el} onChange={props.onChange} />;
        }
      }

      return <Default.Edit el={el} onChange={props.onChange} />;
    },
    Render: (props: { el: Element; onReady?: (el: Element) => void }) => {
      // select the renderer
      const { el } = props;
      if (el.component || el.type) {
        /**
         * Pick a component from the registry and render the element using it
         */
        const Comp = registry.components[el.component?.component || el.type];
        if (Comp) {
          return (
            <Comp.Render
              el={el}
              onReady={(data) => {
                renderResults[data.id] = data;
                if (props.onReady) {
                  props.onReady(data);
                }
              }}
            />
          );
        }
      }
      return (
        <Default.Render
          el={el}
          onReady={(data) => {
            renderResults[data.id] = data;
          }}
        />
      );
    },

    findComponent: (name: TDashboardComponentTypes) => {
      return registry.components[name];
    },

    addComponent: (
      name: TDashboardComponentTypes,
      component: TDashboardComponent
    ) => {
      registry.components[name] = component;
    },
  };
};

export default createComponentRegistry;
